import { MESES } from '@/features/catalog/catalog';
import type { WorkListRow } from '@/features/works/works';
import type { ParticipantResult } from '@/features/dashboard/dashboard';
import type { ClientReportRow, ClosureExport, ClosureExportWork } from './reports';
import { repartoPorCliente, type RepartoDelMes } from '@/features/dashboard/reparto';

/** Lo que expone `MainActivity` para poder entregar un archivo en Android. */
interface PuenteArchivos {
  guardar: (base64: string, nombre: string, mime: string) => void;
}

/** Bytes a base64 sin reventar la pila con archivos grandes. */
function aBase64(bytes: ArrayBuffer): string {
  const u8 = new Uint8Array(bytes);
  let s = '';
  // De a pedazos: `String.fromCharCode(...u8)` con un PDF de un mes entero pasa
  // el límite de argumentos y tira «Maximum call stack size exceeded».
  const paso = 0x8000;
  for (let i = 0; i < u8.length; i += paso) {
    s += String.fromCharCode(...u8.subarray(i, i + paso));
  }
  return btoa(s);
}

/**
 * Entrega el archivo, por el camino que corresponda.
 *
 * En un navegador alcanza con un `<a download>` sobre un `blob:`. **En el
 * WebView de Android eso no hace absolutamente nada**: no hay gestor de
 * descargas, y un `blob:` ni siquiera dispara el `DownloadListener` que se le
 * pueda poner. El PDF se generaba entero y desaparecía en silencio —sin
 * archivo y sin error—, que es la peor manera de fallar: parece que el botón
 * está roto.
 *
 * Dentro de la aplicación se escribe de verdad y se abre con el lector que
 * haya. Fuera, sigue el camino de siempre.
 */
async function descargar(blob: Blob, nombre: string) {
  const puente = (window as unknown as { BoinaArchivos?: PuenteArchivos }).BoinaArchivos;
  if (puente) {
    puente.guardar(aBase64(await blob.arrayBuffer()), nombre, blob.type);
    return;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Generación de archivos.
 *
 * `exceljs` y `jspdf` se cargan con `import()` dinámico, no al arrancar la
 * aplicación: entre las dos pesan más que todo el resto del bundle, y la
 * mayoría de las sesiones desde el campo nunca exportan nada. Vite las separa
 * en chunks propios que se bajan recién al tocar el botón.
 */

/** Los importes van a la planilla como NÚMEROS en pesos, no como texto. */
const pesos = (cents: number | null): number | null => (cents === null ? null : cents / 100);

const FORMATO_PESOS = '"$"#,##0.00';

/**
 * `exceljs` y `jspdf` publican CommonJS. El bundler del navegador lo adapta y
 * expone los nombres directamente; Node los deja bajo `default`. Este ayudante
 * acepta las dos formas, que es lo que permite generar los mismos archivos
 * desde la aplicación y desde un script.
 */
function interop<T>(mod: unknown, nombre: string): T {
  const m = mod as Record<string, unknown> & { default?: Record<string, unknown> };
  return (m[nombre] ?? m.default?.[nombre]) as T;
}

function nombreArchivo(base: string, year: number, month: number | null, ext: string) {
  const periodo = month === null ? `${year}` : `${year}-${String(month).padStart(2, '0')}`;
  return `${base}-${periodo}.${ext}`;
}

// ---------------------------------------------------------------------------
// Excel del período (tarea 9.4)
// ---------------------------------------------------------------------------

export interface ExcelPeriodo {
  organizationName: string;
  year: number;
  month: number;
  works: WorkListRow[];
  byClient: ClientReportRow[];
  /** Sólo lo tiene un administrador; para un miembro llega vacío. */
  participants: ParticipantResult[] | null;
  /** El reparto trabajo por trabajo. También sólo para un administrador. */
  reparto: RepartoDelMes | null;
}

/** Nombre del archivo, para que el que construye y el que descarga coincidan. */
export function nombreExcel(datos: ExcelPeriodo) {
  return nombreArchivo(
    datos.organizationName.replace(/\s+/g, '-'),
    datos.year,
    datos.month,
    'xlsx',
  );
}

/**
 * Arma el libro y devuelve sus bytes. Separado de la descarga a propósito: así
 * el archivo se puede generar también fuera del navegador —para revisarlo o
 * para una prueba— sin tocar `document`.
 */
export async function construirExcel(datos: ExcelPeriodo): Promise<ArrayBuffer> {
  const Workbook = interop<typeof import('exceljs').Workbook>(await import('exceljs'), 'Workbook');
  const wb = new Workbook();
  wb.creator = 'Gestión Veterinaria';
  wb.created = new Date();

  // --- Hoja 1: los trabajos, con su estado administrativo
  const hoja = wb.addWorksheet('Trabajos');
  hoja.columns = [
    { header: 'Fecha', key: 'fecha', width: 12 },
    { header: 'Cliente', key: 'cliente', width: 26 },
    { header: 'Establecimiento', key: 'campo', width: 22 },
    { header: 'Honorarios', key: 'servicios', width: 30 },
    { header: 'kg novillo', key: 'kg', width: 12 },
    { header: 'Honorarios $', key: 'honorarios', width: 15 },
    { header: 'Movilidad $', key: 'movilidad', width: 14 },
    { header: 'Total $', key: 'total', width: 15 },
    { header: 'Facturado', key: 'facturado', width: 11 },
    { header: 'Cobrado', key: 'cobrado', width: 11 },
    { header: 'Factura', key: 'factura', width: 18 },
  ];
  for (const w of datos.works) {
    hoja.addRow({
      fecha: w.work_date,
      cliente: w.client_name,
      campo: w.establishment_name ?? '',
      servicios: w.service_names,
      kg: w.total_kg,
      honorarios: pesos(w.honoraria_cents),
      movilidad: pesos(w.mobility_cents),
      total: pesos(w.total_cents),
      // Un pendiente se dice, no se disfraza de cero.
      facturado: w.is_billed ? 'Sí' : 'No',
      cobrado: w.is_paid ? 'Sí' : 'No',
      factura: w.biller_name,
    });
  }

  // --- Hoja 2: por cliente y establecimiento
  const porCliente = wb.addWorksheet('Por cliente');
  porCliente.columns = [
    { header: 'Cliente', key: 'cliente', width: 26 },
    { header: 'Establecimiento', key: 'campo', width: 22 },
    { header: 'Trabajos', key: 'trabajos', width: 10 },
    { header: 'kg novillo', key: 'kg', width: 12 },
    { header: 'Total $', key: 'total', width: 15 },
    { header: 'Sin precio', key: 'pendientes', width: 11 },
    { header: 'A facturar', key: 'aFacturar', width: 11 },
    { header: 'A cobrar', key: 'aCobrar', width: 11 },
    { header: 'A cobrar $', key: 'aCobrarMonto', width: 15 },
  ];
  for (const c of datos.byClient) {
    porCliente.addRow({
      cliente: c.client_name,
      campo: c.establishment_name ?? '',
      trabajos: c.works_count,
      kg: c.total_kg,
      total: pesos(c.total_cents),
      pendientes: c.pending_count,
      aFacturar: c.pending_to_bill_count,
      aCobrar: c.pending_to_collect_count,
      aCobrarMonto: pesos(c.pending_to_collect_cents),
    });
  }

  // --- Hoja 3: por participante. Sólo si el usuario tiene derecho a verla.
  // No es que la hoja se oculte: si no llegó el dato, no existe la hoja.
  if (datos.participants && datos.participants.length > 0) {
    const porParticipante = wb.addWorksheet('Por participante');
    porParticipante.columns = [
      { header: 'Participante', key: 'nombre', width: 24 },
      { header: 'Honorarios $', key: 'honorarios', width: 15 },
      { header: 'Movilidad $', key: 'movilidad', width: 14 },
      { header: 'Ajustes $', key: 'ajustes', width: 14 },
      { header: 'Facturación asignada $', key: 'facturo', width: 24 },
      { header: 'Saldo $', key: 'saldo', width: 15 },
    ];
    for (const p of datos.participants) {
      porParticipante.addRow({
        nombre: p.display_name,
        honorarios: pesos(p.earned_honoraria_cents),
        movilidad: pesos(p.earned_mobility_cents),
        ajustes: pesos(p.adjustments_cents),
        facturo: pesos(p.billed_cents),
        saldo: pesos(p.balance_cents),
      });
    }
  }

  // --- Hoja 4: cliente por cliente, cuánto es de cada uno.
  //
  // La lee quien atiende la veterinaria cuando llega la plata de un cliente:
  // busca el cliente y entrega. No usa la aplicación, así que la hoja dice
  // nombre y montos y nada más. Se probó con columnas de cobrado, pendientes y
  // totales: para quien la usa era ruido y la volvía difícil de leer.
  //
  // La movilidad ya está adentro del número de cada uno: si el viaje lo puso
  // Felo, lo de Felo es su parte del trabajo más la movilidad, junto.
  if (datos.reparto && datos.reparto.works.length > 0) {
    const { personas, clientes } = repartoPorCliente(datos.reparto);
    const reparto = wb.addWorksheet('Reparto');
    reparto.columns = [
      { header: 'Cliente', key: 'cliente', width: 30 },
      ...personas.map((p) => ({ header: `${p.display_name} $`, key: p.participant_id, width: 18 })),
    ];

    for (const c of clientes) {
      const fila: Record<string, string | number | null> = { cliente: c.client_name };
      for (const p of personas) {
        // Cero explícito, no vacío: que a alguien no le toque nada de este
        // cliente es una respuesta, y se lee distinto que una celda en blanco.
        fila[p.participant_id] = pesos(c.porPersona.get(p.participant_id) ?? 0);
      }
      reparto.addRow(fila);
    }
  }

  for (const ws of wb.worksheets) {
    ws.getRow(1).font = { bold: true };
    ws.views = [{ state: 'frozen', ySplit: 1 }];
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: ws.columnCount } };
    for (const col of ws.columns) {
      if (typeof col.header === 'string' && col.header.includes('$')) {
        col.numFmt = FORMATO_PESOS;
      }
    }
  }

  return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
}

export async function exportarExcel(datos: ExcelPeriodo) {
  const bytes = await construirExcel(datos);
  await descargar(
    new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    nombreExcel(datos),
  );
}

// ---------------------------------------------------------------------------
// PDF del cierre, DESDE EL SNAPSHOT (tarea 9.5)
// ---------------------------------------------------------------------------

const money = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
});
const fmt = (cents: number | null) => (cents === null ? 'Pendiente' : money.format(cents / 100));

/**
 * El PDF se arma con lo que devuelve `closure_export`, que lee
 * `closure_work_snapshots`. Nada acá recalcula importes: si mañana cambia el
 * precio de referencia, este documento sigue diciendo lo mismo.
 */
export function nombrePdfCierre(datos: ClosureExport) {
  return nombreArchivo(
    `cierre-${datos.organization_name}-v${datos.version_no}`.replace(/\s+/g, '-'),
    datos.period_year,
    datos.period_month,
    'pdf',
  );
}

export async function construirPdfCierre(datos: ClosureExport): Promise<ArrayBuffer> {
  const [jspdfMod, autoTableMod] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  const jsPDF = interop<typeof import('jspdf').jsPDF>(jspdfMod, 'jsPDF');
  const autoTable =
    interop<typeof import('jspdf-autotable').default>(autoTableMod, 'default') ??
    autoTableMod.default;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const titulo = `${MESES[datos.period_month - 1]} ${datos.period_year}`;

  doc.setFontSize(16);
  doc.text(datos.organization_name, 40, 50);
  doc.setFontSize(12);
  doc.text(`Liquidación · ${titulo}`, 40, 70);

  doc.setFontSize(9);
  const cerradoEl = new Date(datos.closed_at).toLocaleString('es-AR');
  doc.text(
    `Versión ${datos.version_no}${datos.is_current ? ' (vigente)' : ''} · cerrada el ${cerradoEl}` +
      (datos.closed_by_name ? ` por ${datos.closed_by_name}` : ''),
    40,
    88,
  );
  if (datos.reopen_reason) {
    doc.text(`Reabierta posteriormente: ${datos.reopen_reason}`, 40, 102);
  }

  autoTable(doc, {
    startY: datos.reopen_reason ? 118 : 106,
    head: [['Fecha', 'Cliente', 'Honorarios', 'kg', 'Honorarios $', 'Movilidad $', 'Total $']],
    body: datos.works.map((w) => [
      w.work_date,
      w.establishment_name ? `${w.client_name} — ${w.establishment_name}` : w.client_name,
      w.services ?? '',
      String(w.total_kg),
      fmt(w.honoraria_cents),
      fmt(w.mobility_cents),
      fmt(w.total_cents),
    ]),
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [30, 41, 59] },
    columnStyles: {
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
    },
  });

  const totalPeriodo = datos.works.reduce((acc, w) => acc + (w.total_cents ?? 0), 0);

  autoTable(doc, {
    head: [['Participante', 'Le corresponde', 'Ajustes', 'Facturación asignada', 'Saldo']],
    body: datos.entries.map((e) => [
      e.display_name,
      fmt(e.earned_honoraria_cents + e.earned_mobility_cents),
      fmt(e.adjustments_cents),
      fmt(e.billed_cents),
      fmt(e.balance_cents),
    ]),
    foot: [['Total del período', '', '', '', fmt(totalPeriodo)]],
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [30, 41, 59] },
    footStyles: { fillColor: [241, 245, 249], textColor: 20 },
    columnStyles: {
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
    },
  });

  agregarDetalleParaFacturar(doc, autoTable, datos);

  if (datos.transfers.length > 0) {
    autoTable(doc, {
      head: [['Transferencias', 'Importe']],
      // Sin flecha Unicode: las fuentes estándar de jsPDF usan WinAnsi y la
      // convierten en un glifo basura ("Manuel !' Felipe").
      body: datos.transfers.map((t) => [`${t.from_name}  ->  ${t.to_name}`, fmt(t.amount_cents)]),
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [30, 41, 59] },
      columnStyles: { 1: { halign: 'right' } },
    });
  }

  return doc.output('arraybuffer');
}

/**
 * Los trabajos agrupados por cliente, para poder facturar sin abrir la
 * aplicación.
 *
 * ## Qué problema resuelve
 *
 * La tabla de arriba alcanza para repartir la plata y no para facturar. Para
 * facturar hay que ir cliente por cliente, y un cliente al que se le fue dos
 * veces en el mes aparecía en dos renglones separados por diez líneas. Se
 * terminaba anotando aparte, en papel, lo que el documento ya tenía.
 *
 * ## Las decisiones de armado
 *
 * **Agrupado por cliente, ordenado por fecha.** Los dos trabajos de un mismo
 * cliente quedan pegados, cada uno con su fecha al lado. **No se suman los
 * kilos**: son dos visitas distintas y facturarlas juntas es perder de vista
 * qué se hizo cada día. Lo que sí se suma es el subtotal del cliente, que es lo
 * que va en el comprobante.
 *
 * **Los bloques de cliente van por su primer trabajo del mes**, no alfabéticos:
 * leyendo de arriba abajo el mes avanza, que es como se recorre una planilla
 * cuando se está facturando.
 *
 * **Quién factura va resaltado.** Es el dato que decide a nombre de quién sale
 * el comprobante, y buscarlo en un renglón gris es exactamente donde se comete
 * el error que después hay que anular.
 */
function agregarDetalleParaFacturar(
  doc: import('jspdf').jsPDF,
  autoTable: typeof import('jspdf-autotable').default,
  datos: ClosureExport,
) {
  if (datos.works.length === 0) return;

  // Agrupar conservando el orden de llegada: la consulta ya devuelve los
  // trabajos por fecha, así que el primero de cada cliente fija la posición de
  // su bloque y los de adentro quedan ordenados solos.
  const porCliente = new Map<string, ClosureExportWork[]>();
  for (const w of datos.works) {
    const clave = w.establishment_name
      ? `${w.client_name} — ${w.establishment_name}`
      : w.client_name;
    const grupo = porCliente.get(clave);
    if (grupo) grupo.push(w);
    else porCliente.set(clave, [w]);
  }

  const fecha = (iso: string) => iso.slice(8, 10) + '/' + iso.slice(5, 7);

  const filas: (string | { content: string; colSpan?: number; styles?: object })[][] = [];

  for (const [cliente, trabajos] of porCliente) {
    const subtotal = trabajos.reduce((acc, w) => acc + (w.total_cents ?? 0), 0);
    const pendiente = trabajos.some((w) => w.total_cents === null);

    filas.push([
      {
        content: cliente,
        styles: { fontStyle: 'bold', fillColor: [241, 245, 249] },
      },
      {
        content: trabajos.length === 1 ? '' : `${trabajos.length} visitas`,
        styles: { fillColor: [241, 245, 249] },
      },
      {
        // Un subtotal que suma importes pendientes mentiría por lo bajo.
        content: pendiente ? 'Pendiente' : money.format(subtotal / 100),
        styles: { fontStyle: 'bold', halign: 'right', fillColor: [241, 245, 249] },
      },
    ]);

    for (const w of trabajos) {
      const renglones = [
        `${fecha(w.work_date)}  ${w.services ?? ''}`.trim(),
        `${w.total_kg} kg · honorarios ${fmt(w.honoraria_cents)}`,
      ];
      if ((w.mobility_cents ?? 0) !== 0) {
        renglones.push(
          `Movilidad ${fmt(w.mobility_cents)}${w.mobility_for ? ` (${w.mobility_for})` : ''}`,
        );
      }
      if (w.description && w.description.trim() !== '') {
        // En un renglón solo: se escribe en varias líneas y aplastarlo lo
        // vuelve ilegible justo cuando se lo está leyendo para descontar.
        renglones.push(`Gastos: ${w.description.trim().split('\n').join(', ')}`);
      }
      if (w.splits.length > 0) {
        renglones.push('Reparto: ' + w.splits.map((s) => `${s.name} ${s.percentage}%`).join(' · '));
      }

      filas.push([
        { content: renglones.join('\n'), styles: { fontStyle: 'normal' } },
        {
          content:
            `Factura ${w.biller_name ?? 'sin asignar'}` +
            (w.payment_recipient_name && w.payment_recipient_name !== w.biller_name
              ? `\nCobra ${w.payment_recipient_name}`
              : ''),
          styles: { fontStyle: 'bold' },
        },
        { content: fmt(w.total_cents), styles: { halign: 'right' } },
      ]);
    }
  }

  autoTable(doc, {
    head: [['Detalle para facturar', 'Factura', 'Total $']],
    body: filas,
    styles: { fontSize: 8, cellPadding: 4, valign: 'top' },
    headStyles: { fillColor: [30, 41, 59] },
    columnStyles: { 0: { cellWidth: 300 }, 2: { halign: 'right' } },
  });
}

export async function exportarPdfCierre(datos: ClosureExport) {
  const bytes = await construirPdfCierre(datos);
  await descargar(new Blob([bytes], { type: 'application/pdf' }), nombrePdfCierre(datos));
}
